import {User} from './types';

const Actions = ['create', 'delete', 'get', 'list', 'update'] as const;
const Resources = ['signup.rule', 'signup.waitlist'] as const;

export type Action = (typeof Actions)[number];
export type Resource = (typeof Resources)[number];
export type Permission = `${Action}:${Resource}`;

function generatePermissions(resource: Resource, ...actions: Action[]): Permission[] {
  return actions.map((action) => `${action}:${resource}`).map((permission) => permission as Permission);
}

/**
 * Mapping of Roles to the Permissions they grant.
 * This allows us to add/remove permissions from a role without migrating user documents.
 */
const RolePermissions: Record<string, Permission[]> = {
  administrator: Resources.flatMap((resource) => generatePermissions(resource, ...Actions)),
  // Add other roles here as the app grows
  // editor: [...],
  // viewer: [...],
};

/**
 * Checks if a user has a specific permission for a given target.
 *
 * @param user The user document.
 * @param permission The granular permission to check (e.g., 'create:signup-rules').
 * @param target The target scope (e.g., an organization ID or '*'). Defaults to '*'.
 * @returns true if the user has the permission; false otherwise.
 */
export function hasPermission(user: User, permission: Permission, target: string = '*'): boolean {
  const auth = user.__auth;
  if (!auth) {
    return false;
  }

  // 1. Global administrators have all permissions.
  if (auth.roles?.includes('administrator:*')) {
    return true;
  }

  // 2. Check global or scoped materialized permissions.
  if (target === '*') {
    if (auth.permissions?.global?.[permission] === true) {
      return true;
    }
  } else {
    // Check scoped materialized permissions if present.
    const scopedPermissions = auth.permissions as Record<string, Record<string, boolean> | undefined> | undefined;
    if (scopedPermissions?.[target]?.[permission] === true) {
      return true;
    }
  }

  // 3. Fallback to roles-based check for backward compatibility.
  if (!auth.roles || auth.roles.length === 0) {
    return false;
  }

  return auth.roles.some((roleTuple) => {
    const [roleName, roleTarget] = roleTuple.split(':');

    // Check if the target scope matches (or is a global assignment).
    const targetMatches = roleTarget === '*' || roleTarget === target;
    if (!targetMatches) {
      return false;
    }

    // Resolve role name to its defined permissions.
    const permissions = RolePermissions[roleName] || [];
    return permissions.includes(permission);
  });
}
